import { getAuth } from 'firebase/auth';

/**
 * Get Firebase auth token for API authentication
 */
async function getAuthToken() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return await user.getIdToken();
}

/**
 * Sanitize a filename for safe GCS storage.
 * Strips path separators, special chars, multiple dots, and normalizes whitespace.
 * Preserves the extension.
 */
function sanitizeFileName(name) {
  if (!name) return 'contrato.pdf';

  const lastDot = name.lastIndexOf('.');
  const ext = lastDot > 0 ? name.slice(lastDot).toLowerCase() : '.pdf';
  const base = lastDot > 0 ? name.slice(0, lastDot) : name;

  const cleaned = base
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[/\\:*?"<>|#[\]{}%&$@!^`~;=+]/g, '')
    .replace(/\.+/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 100);

  return (cleaned || 'contrato') + ext;
}

/**
 * Upload a contract file for a convenio.
 *
 * The backend handles everything:
 * - Generates contractId server-side
 * - Creates contracts/{contractId} doc with status: 'procesando'
 * - Uploads file to GCS
 * - Sends to DocumentAI for OCR
 * - On OCR completion: extracts fields via GPT, updates contract doc to 'procesado'
 * - Updates convenios/{convenioId}.contracts array
 *
 * @param {File} file - The contract PDF file
 * @param {Object} params - Upload parameters
 * @param {string} params.convenioId - Convenio ID
 * @param {string} params.beneficiarioId - Beneficiario ID within the convenio
 * @param {string} [params.proyectoId] - Optional project ID within the convenio
 * @returns {Promise<Object>} { status: 'success', data: { contractId, convenioId, processingQueued } }
 */
export async function uploadConvenioContract(file, { convenioId, beneficiarioId, proyectoId }) {
  const token = await getAuthToken();
  const API_BASE = process.env.REACT_APP_API_URL;

  const safeName = sanitizeFileName(file.name);
  const safeFile = new File([file], safeName, { type: file.type });

  console.log('[convenioUpload] POST /upload-convenio-contract', {
    fileName: safeName,
    originalName: file.name,
    fileSize: file.size,
    fileType: file.type,
    convenioId,
    beneficiarioId,
    proyectoId: proyectoId || null,
    apiBase: API_BASE,
  });

  const formData = new FormData();
  formData.append('file', safeFile);
  formData.append('convenioId', convenioId);
  formData.append('beneficiarioId', beneficiarioId);
  if (proyectoId) formData.append('proyectoId', proyectoId);

  const response = await fetch(`${API_BASE}/upload-convenio-contract`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    if (response.status === 429) {
      throw new Error(errorData.reason || 'Límite de páginas excedido para esta organización');
    }

    throw new Error(errorData.error || `Upload failed: ${response.status}`);
  }

  return await response.json();
}
